import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { fetchStatePrint, saveQuarterMeta } from '../lib/api';
import { AREE, AREE_ORDER } from '../constants/areas';
import { labelSettimana } from '../lib/periodFormat';
import './Stampa.css';

const fmtPad = (n) => String(n).padStart(2, '0');
const fmtNum = (v, unita) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  let s;
  if (Math.abs(n) >= 10000) {
    s = (n / 1000).toLocaleString('it-IT', { maximumFractionDigits: 1 }) + 'k';
  } else {
    s = Number.isInteger(n) ? n.toLocaleString('it-IT') : n.toLocaleString('it-IT', { maximumFractionDigits: 2 });
  }
  return unita ? `${s} ${unita}` : s;
};
const fmtRaw = (v, unita) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  const s = Number.isInteger(n) ? n.toLocaleString('it-IT') : n.toLocaleString('it-IT', { maximumFractionDigits: 1 });
  return unita ? `${s} ${unita}` : s;
};

const AREA_VAR = { business: 'c-business', lavoro: 'c-lavoro', personale: 'c-personale', salute: 'c-salute' };

const Q_LABEL = {
  1: { num: 'Q1', mesi: 'gen · feb · mar' },
  2: { num: 'Q2', mesi: 'apr · mag · giu' },
  3: { num: 'Q3', mesi: 'lug · ago · set' },
  4: { num: 'Q4', mesi: 'ott · nov · dic' },
};

const GIORNI_IT = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

// ── Scope della stampa ────────────────────────────────────────────────
// Permette all'utente di generare PDF più corti scegliendo il livello di dettaglio.
// Ogni scope determina quali sezioni sono renderizzate. Il default è 'tutto'
// per retrocompatibilità con il link 'Stampa · PDF' già in sidebar.
const SCOPES = [
  { id: 'tutto', label: 'Tutto', hint: 'Anno + 4 quarter + mese + settimane' },
  { id: 'quarter', label: 'Quarter', hint: 'Quarter corrente + mese + settimane' },
  { id: 'mese', label: 'Mese', hint: 'Mese corrente + settimane' },
  { id: 'settimana', label: 'Settimana', hint: 'Solo settimana corrente (A4, 1 pagina compatta)' },
  { id: 'settimana-giorni', label: 'Settimana + giorni', hint: 'Settimana + una pagina per giorno' },
  { id: 'settimana-portafoglio', label: 'Settimana · portafoglio', hint: 'A5 landscape, foldable in tasca: 2 colonne × 4 righe' },
];

// ── Utility per settimana ISO / current week label ────────────────────
function isoWeekLabelToday() {
  const t = new Date();
  const d = new Date(Date.UTC(t.getFullYear(), t.getMonth(), t.getDate()));
  const dow = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dow);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const wn = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(wn).padStart(2, '0')}`;
}

function quarterFromMonth(m) {
  return Math.floor((m - 1) / 3) + 1;
}

// ── Sparkline confidence (SVG inline, stampabile) ──
function PrintSparkline({ history }) {
  if (!history || history.length === 0) {
    return <span className="spark-empty">—</span>;
  }
  const vals = history.map((h) => Number(h.confidence) || 0);
  const w = 70, h = 16;
  const stepX = vals.length > 1 ? w / (vals.length - 1) : 0;
  const points = vals.map((v, i) => `${(i * stepX).toFixed(1)},${(h - v * h).toFixed(1)}`).join(' ');
  const last = vals[vals.length - 1];
  return (
    <span className="spark">
      <svg width={w} height={h} className="spark-svg">
        <polyline fill="none" stroke="#0B0B0F" strokeWidth="1" points={points} />
        {vals.map((v, i) => (
          <circle key={`p-${i}`} cx={i * stepX} cy={h - v * h} r="1" fill={i === vals.length - 1 ? '#E5484D' : '#0B0B0F'} />
        ))}
      </svg>
      <span className="spark-val">{last?.toFixed(2)}</span>
    </span>
  );
}

// ── ISO week → date di lunedì (UTC) ──
function mondayOfIsoWeek(yearW) {
  const [y, w] = yearW.split('-W').map(Number);
  // Jan 1 weekday (Mon=1..Sun=7)
  const jan1Day = (new Date(Date.UTC(y, 0, 1)).getUTCDay() || 7);
  // ISO: settimana 1 contiene il primo giovedì
  const firstThursday = new Date(Date.UTC(y, 0, 1 + ((4 - jan1Day + 7) % 7)));
  const monday = new Date(firstThursday);
  monday.setUTCDate(firstThursday.getUTCDate() - 3 + (w - 1) * 7);
  return monday;
}

function fmtDateIt(d) {
  return `${fmtPad(d.getUTCDate())}/${fmtPad(d.getUTCMonth() + 1)}`;
}
function isoDateUTC(d) {
  return `${d.getUTCFullYear()}-${fmtPad(d.getUTCMonth() + 1)}-${fmtPad(d.getUTCDate())}`;
}

export default function Stampa() {
  const [data, setData] = useState(null);
  // Stato locale per editing inline focus/rischi (mappa quarter -> {focus, rischi})
  const [qMeta, setQMeta] = useState({});
  // Scope della stampa (URL-driven) — default 'tutto' per retrocompat con vecchi link
  const [searchParams, setSearchParams] = useSearchParams();
  const scopeParam = searchParams.get('scope') || 'tutto';
  const scope = SCOPES.find((s) => s.id === scopeParam) ? scopeParam : 'tutto';
  const changeScope = (newId) => {
    // Aggiorna URL così l'utente può bookmark/condividere il link con lo scope preferito
    const next = new URLSearchParams(searchParams);
    if (newId === 'tutto') next.delete('scope');
    else next.set('scope', newId);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    fetchStatePrint().then((d) => {
      setData(d);
      // Seed qMeta dai dati del backend
      const seed = {};
      [1, 2, 3, 4].forEach((q) => {
        const m = (d.quarters_meta || {})[q] || (d.quarters_meta || {})[String(q)] || {};
        seed[q] = { focus: m.focus || '', rischi: m.rischi || '' };
      });
      setQMeta(seed);
    });
  }, []);

  if (!data) {
    return <div className="min-h-screen bg-canvas flex items-center justify-center"><div className="mono-label">Generazione planner…</div></div>;
  }

  const anno = data.anno?.anno;
  const tema = data.anno?.tema || '';
  const perche = data.anno?.perche || '';
  const numeri = data.numeri_importanti || [];
  const obiettivi = data.obiettivi || [];
  const krs = data.key_results || [];
  const tappeByQ = data.tappe_by_quarter || {};
  const krNumerici = krs.filter((k) => k.tipo === 'numerico');
  const confByKr = data.confidence_history_by_kr || {};
  const mitMese = data.mit_mese || [];
  const settimaneMese = data.mese_corrente?.settimane || [];

  // Raggruppo MIT per data ISO (YYYY-MM-DD)
  const mitByDate = mitMese.reduce((m, x) => {
    const k = x.data;
    if (!k) return m;
    m[k] = m[k] || [];
    m[k].push(x);
    return m;
  }, {});

  const grouped = AREE_ORDER
    .map((a) => ({ area: a, items: obiettivi.filter((o) => (o.area || '').toLowerCase() === a) }))
    .filter((g) => g.items.length > 0);

  const currentWeekLabel = isoWeekLabelToday();
  const currentQuarter = quarterFromMonth(data.mese_corrente?.num || 1);

  // ── Visibility flags derivate dallo scope ──────────────────────────
  // Ogni scope include TUTTO ciò che sta "sotto" nel suo cono:
  //  - tutto             = anno + tutti i quarter + mese + settimane del mese
  //  - quarter           = solo quarter corrente + mese + settimane del mese
  //  - mese              = solo mese + settimane del mese
  //  - settimana         = solo la settimana corrente (1 pagina compatta)
  //  - settimana-giorni  = come 'settimana' + 7 pagine giorno
  const vis = {
    anno: scope === 'tutto',
    quarterTargets: scope === 'tutto' || scope === 'quarter',
    quarterScomp: scope === 'tutto' || scope === 'quarter',
    mese: scope === 'tutto' || scope === 'quarter' || scope === 'mese',
    weekLabels: (scope === 'settimana' || scope === 'settimana-giorni')
      ? [currentWeekLabel]
      : (scope === 'tutto' || scope === 'quarter' || scope === 'mese') ? settimaneMese : [],
    days: scope === 'settimana-giorni',
    // Portafoglio: singola pagina A5 landscape, layout 2 colonne × 4 righe, foldable
    portafoglio: scope === 'settimana-portafoglio',
  };

  // Quali quarter visualizzare nelle pagine target/pagella?
  const visibleQuarters = scope === 'tutto' ? [1, 2, 3, 4]
    : scope === 'quarter' ? [currentQuarter]
    : [];

  // Ricalcolo pagine effettive per il badge in toolbar
  const totalPages = (vis.anno ? 1 : 0)
    + (vis.quarterTargets ? visibleQuarters.length : 0)
    + (vis.quarterScomp ? visibleQuarters.length : 0)
    + (vis.mese ? 1 : 0)
    + vis.weekLabels.length
    + (vis.days ? 7 : 0)
    + (vis.portafoglio ? 1 : 0);
  const footPage = (n) => `${fmtPad(n)}/${fmtPad(totalPages)}`;

  // Save handler per focus/rischi (auto-save on blur)
  const handleSaveMeta = async (q) => {
    const local = qMeta[q] || {};
    const remote = (data.quarters_meta || {})[q] || (data.quarters_meta || {})[String(q)] || {};
    if ((local.focus || '') === (remote.focus || '') && (local.rischi || '') === (remote.rischi || '')) {
      return; // nessun cambio
    }
    try {
      await saveQuarterMeta({ quarter: q, focus: local.focus || null, rischi: local.rischi || null });
      toast.success(`Q${q} · focus e rischi salvati`);
      // aggiorna data.quarters_meta locale per evitare doppio save
      setData((d) => ({
        ...d,
        quarters_meta: { ...(d.quarters_meta || {}), [q]: { ...(d.quarters_meta?.[q] || {}), focus: local.focus, rischi: local.rischi } },
      }));
    } catch (e) {
      toast.error(`Errore salvataggio Q${q}: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div className={`qp-print-wrap${vis.portafoglio ? ' qp-print-wallet' : ''}`}>
      <div className="qp-toolbar no-print" data-testid="print-toolbar">
        <Link to="/" className="qp-toolbar-back" data-testid="back-to-dashboard">
          <ArrowLeft size={14} strokeWidth={1.5} /> Dashboard
        </Link>
        <div className="qp-toolbar-scope">
          <label htmlFor="print-scope-select" className="qp-toolbar-scope-lbl">
            Estensione
          </label>
          <select
            id="print-scope-select"
            value={scope}
            onChange={(e) => changeScope(e.target.value)}
            className="qp-toolbar-scope-sel"
            data-testid="print-scope-select"
            title={SCOPES.find((s) => s.id === scope)?.hint}
          >
            {SCOPES.map((s) => (
              <option key={s.id} value={s.id} title={s.hint}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="qp-toolbar-title" data-testid="print-total-pages">
          {totalPages} pagin{totalPages === 1 ? 'a' : 'e'}
        </div>
        <button onClick={() => window.print()} className="qp-toolbar-print" data-testid="print-pdf-btn">
          <Printer size={14} strokeWidth={1.5} /> Scarica PDF
        </button>
      </div>

      {/* PAGINA 1 — ANNO */}
      {vis.anno && (
      <section className="page" data-testid="print-page-anno">
        <PageHeader anno={anno} />
        <div className="hero">
          <div>
            <div className="kick">Anno</div>
            <h1>{anno}</h1>
          </div>
          <div className="sub">Tema<br />dell&apos;anno</div>
        </div>

        {(tema || perche) && (
          <div className="tema">
            <div className="barv"></div>
            <div>
              <div className="lab">Tema · perché</div>
              {tema && <div className="txt">{tema}</div>}
              {perche && <div className="txt" style={{ marginTop: 6, fontWeight: 400, color: 'var(--ink2)' }}>{perche}</div>}
            </div>
          </div>
        )}

        {numeri.length > 0 && <>
          <SectionHeader label="Numeri importanti" hint={`North-star · ${numeri.length}`} />
          <table className="tbl"><tbody>
            {numeri.map((n) => (
              <tr key={n.id}>
                <td className="k" style={{ width: 40 }}><span className="onum">{fmtPad(n.ordine || 0)}</span></td>
                <td>{n.descrizione}</td>
                <td className="m" style={{ textAlign: 'right' }}>{n.metrica}</td>
              </tr>
            ))}
          </tbody></table>
        </>}

        <SectionHeader label={`Obiettivi · ${obiettivi.length}`} />
        {grouped.map((g) => {
          const meta = AREE[g.area]; const cssVar = AREA_VAR[g.area];
          return (
            <div key={g.area} className="block">
              <div className="bhead">
                <span className="tick" style={{ background: `var(--${cssVar})` }}></span>
                <span className="name" style={{ color: `var(--${cssVar})` }}>{meta.label}</span>
                <span className="ct">· {g.items.length}</span>
                <span className="ln"></span>
              </div>
              <div className="objs">
                {g.items.map((o) => {
                  const obKrs = krs.filter((k) => k.obiettivo_id === o.id);
                  return (
                    <div className="obj" key={o.id}>
                      <div className="top">
                        <span className="idx">{fmtPad(o.ordine)}</span>
                        <span className="nm" style={{ color: `var(--${cssVar})` }}>{o.titolo}</span>
                      </div>
                      {o.perche && <div className="note">{o.perche}</div>}
                      {obKrs.map((k) => (
                        <div className="kr" key={k.id}>
                          <span className="tag">{k.natura || k.tipo}</span>
                          <span style={{ flex: 1 }}>{k.titolo}</span>
                          {k.tipo === 'numerico' && (
                            <span className="num">{fmtRaw(k.valore_attuale, k.unita)} / {fmtRaw(k.target_committed, k.unita)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        <Foot label={`ANNO · ${footPage(1)}`} />
      </section>
      )}

      {/* PAGINE 2-5 — QUARTER Q1-Q4 */}
      {vis.quarterTargets && visibleQuarters.map((q) => {
        const qInfo = Q_LABEL[q];
        const tappe = tappeByQ[String(q)] || tappeByQ[q] || [];
        const meta = qMeta[q] || { focus: '', rischi: '' };
        return (
          <section className="page" key={`q${q}`} data-testid={`print-page-q${q}`}>
            <PageHeader anno={anno} />
            <div className="hero sm">
              <div>
                <div className="kick">Trimestre</div>
                <h1>{qInfo.num}</h1>
              </div>
              <div className="sub">{qInfo.mesi.replace(/ · /g, '\n')}</div>
            </div>

            {/* Focus del quarter — editabile a schermo, statico in stampa */}
            <SectionHeader label={`Focus del ${qInfo.num}`} hint="1-3 frasi" />
            <textarea
              className="qmeta"
              data-testid={`q${q}-focus-textarea`}
              value={meta.focus}
              placeholder={`Cosa devi assolutamente portare a casa nel ${qInfo.num}? Scrivi qui e si salva uscendo dal campo.`}
              onChange={(e) => setQMeta((m) => ({ ...m, [q]: { ...m[q], focus: e.target.value } }))}
              onBlur={() => handleSaveMeta(q)}
              rows={3}
            />

            {/* Rischi / dipendenze */}
            <SectionHeader label="Rischi / dipendenze" hint="cosa può bloccarti" />
            <textarea
              className="qmeta"
              data-testid={`q${q}-rischi-textarea`}
              value={meta.rischi}
              placeholder={`Rischi noti, dipendenze esterne, vincoli (1 per riga). Salva uscendo dal campo.`}
              onChange={(e) => setQMeta((m) => ({ ...m, [q]: { ...m[q], rischi: e.target.value } }))}
              onBlur={() => handleSaveMeta(q)}
              rows={3}
            />

            <SectionHeader label="Target a fine quarter" hint={`Cumulativi · ${krNumerici.length} KR`} />
            <table className="tbl">
              <thead><tr>
                <th>Key result</th>
                <th style={{ width: 80, textAlign: 'right' }}>fine {qInfo.num}</th>
                <th style={{ width: 100 }}>confidence</th>
              </tr></thead>
              <tbody>
                {krNumerici.map((k) => {
                  const qv = (k.quarters || []).find((x) => x.etichetta === `Q${q}`);
                  const cssVar = AREA_VAR[(k.area || '').toLowerCase()] || 'ink';
                  return (
                    <tr key={k.id}>
                      <td className="k">
                        <span className="dot" style={{ background: `var(--${cssVar})` }}></span>
                        <span className="onum">{fmtPad(k.obiettivo_ordine)}</span>{' '}{k.titolo}
                      </td>
                      <td className="m" style={{ textAlign: 'right', fontWeight: 600 }}>{fmtNum(qv?.suggerito, k.unita)}</td>
                      <td><PrintSparkline history={confByKr[k.id]} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {tappe.length > 0 && <>
              <SectionHeader label="Tappe del quarter" hint={`${tappe.length}`} />
              <table className="tbl"><tbody>
                {tappe.map((t) => {
                  const cssVar = AREA_VAR[(t.area || '').toLowerCase()] || 'ink';
                  return (
                    <tr key={t.id}>
                      <td className="k" style={{ width: 40 }}><span className="onum">{fmtPad(t.obiettivo_ordine)}</span></td>
                      <td><span className="dot" style={{ background: `var(--${cssVar})` }}></span>{t.titolo}</td>
                      <td className="m" style={{ textAlign: 'right', width: 100 }}>{t.scadenza}</td>
                    </tr>
                  );
                })}
              </tbody></table>
            </>}

            <Foot label={`QUARTER ${qInfo.num} · ${footPage(q + 1)}`} />
          </section>
        );
      })}

      {/* PAGINE 6-9 — SCOMPOSIZIONE MESI per Q */}
      {vis.quarterScomp && visibleQuarters.map((q) => {
        const months = [(q - 1) * 3 + 1, (q - 1) * 3 + 2, (q - 1) * 3 + 3];
        const qInfo = Q_LABEL[q];
        return (
          <section className="page" key={`qm${q}`} data-testid={`print-page-qm${q}`}>
            <PageHeader anno={anno} />
            <div className="hero sm">
              <div>
                <div className="kick">{qInfo.num} · scomposizione</div>
                <h1>{qInfo.mesi}</h1>
              </div>
              <div className="sub">Target<br />per mese</div>
            </div>

            <SectionHeader label="Target cumulato per mese" />
            <table className="tbl">
              <thead><tr>
                <th>Key result</th>
                {months.map((m, i) => {
                  const mnome = (krNumerici[0]?.months || []).find((x) => x.etichetta === `${anno}-${fmtPad(m)}`)?.mese_nome || '';
                  return <th key={m} style={{ textAlign: 'right' }}>{mnome.toLowerCase()}{i === 2 ? ` = fine ${qInfo.num}` : ''}</th>;
                })}
              </tr></thead>
              <tbody>
                {krNumerici.map((k) => {
                  const cssVar = AREA_VAR[(k.area || '').toLowerCase()] || 'ink';
                  return (
                    <tr key={k.id}>
                      <td className="k">
                        <span className="dot" style={{ background: `var(--${cssVar})` }}></span>
                        <span className="onum">{fmtPad(k.obiettivo_ordine)}</span>{' '}{k.titolo}
                      </td>
                      {months.map((m, i) => {
                        const mv = (k.months || []).find((x) => x.etichetta === `${anno}-${fmtPad(m)}`);
                        return <td key={m} className="m" style={{ textAlign: 'right', fontWeight: i === 2 ? 600 : 400 }}>{fmtNum(mv?.suggerito, k.unita)}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <SectionHeader label={`Pagella fine ${qInfo.num}`} hint="da compilare" />
            <table className="tbl">
              <thead><tr><th>Key result</th><th style={{ width: 70 }}>voto 0–1</th><th>perché</th><th style={{ width: 110 }}>porto avanti</th></tr></thead>
              <tbody>
                {krNumerici.slice(0, 5).map((k) => (
                  <tr key={k.id}><td className="k">{k.titolo}</td><td className="blank"></td><td></td><td></td></tr>
                ))}
              </tbody>
            </table>
            <Foot label={`${qInfo.num} SCOMPOSIZIONE · ${footPage(5 + q)}`} />
          </section>
        );
      })}

      {/* PAGINA 10 — MESE CORRENTE con sparkline */}
      {vis.mese && (
      <section className="page" data-testid="print-page-mese">
        <PageHeader anno={anno} />
        <div className="hero sm">
          <div>
            <div className="kick">Mese in corso</div>
            <h1>{data.mese_corrente?.label?.split(' ')[0]}</h1>
          </div>
          <div className="sub">Mese {fmtPad(data.mese_corrente?.num)}<br />{anno}</div>
        </div>

        <SectionHeader label="Target del mese" hint={`${data.mese_corrente?.label}`} />
        <table className="tbl">
          <thead><tr>
            <th>Key result</th>
            <th style={{ textAlign: 'right', width: 70 }}>suggerito</th>
            <th style={{ textAlign: 'right', width: 60 }}>reale</th>
            <th style={{ textAlign: 'right', width: 40 }}>conf.</th>
            <th style={{ width: 100 }}>trend</th>
          </tr></thead>
          <tbody>
            {krNumerici.map((k) => {
              const mv = (k.months || []).find((x) => x.etichetta === `${anno}-${fmtPad(data.mese_corrente?.num)}`);
              const cssVar = AREA_VAR[(k.area || '').toLowerCase()] || 'ink';
              return (
                <tr key={k.id}>
                  <td className="k">
                    <span className="dot" style={{ background: `var(--${cssVar})` }}></span>
                    <span className="onum">{fmtPad(k.obiettivo_ordine)}</span>{' '}{k.titolo}
                  </td>
                  <td className="m" style={{ textAlign: 'right' }}>{fmtNum(mv?.suggerito, k.unita)}</td>
                  <td className="m" style={{ textAlign: 'right' }}>{fmtRaw(k.valore_attuale, k.unita)}</td>
                  <td className="m" style={{ textAlign: 'right' }}>{k.confidence != null ? Number(k.confidence).toFixed(1) : '—'}</td>
                  <td><PrintSparkline history={confByKr[k.id]} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <SectionHeader label="Azioni settimanali del mese" hint={`${(data.azioni_mese || []).length} pianificate`} />
        {(data.mese_corrente?.settimane || []).map((w) => {
          const list = (data.azioni_mese || []).filter((a) => a.settimana === w);
          if (list.length === 0) return null;
          return (
            <div key={w} style={{ marginBottom: '3mm' }}>
              <div className="bhead">
                <span className="name" style={{ fontSize: 9 }}>{labelSettimana(w)}</span>
                <span className="ln"></span>
                <span className="ct">{list.length}</span>
              </div>
              <table className="tbl"><tbody>
                {list.map((a) => {
                  const cssVar = AREA_VAR[(a.area || '').toLowerCase()] || 'ink';
                  return (
                    <tr key={a.id}>
                      <td style={{ width: 50 }}>
                        <span className="tag" style={{ borderColor: `var(--${cssVar})`, color: `var(--${cssVar})` }}>
                          {a.is_bau ? 'BAU' : (a.area || '')}
                        </span>
                      </td>
                      <td>{a.testo}</td>
                    </tr>
                  );
                })}
              </tbody></table>
            </div>
          );
        })}
        <Foot label={`MESE · ${footPage(10)}`} />
      </section>
      )}

      {/* PAGINE 11+ — SETTIMANE GIORNALIERE (MIT) del mese corrente */}
      {vis.weekLabels.map((wLabel, idx) => {
        const monday = mondayOfIsoWeek(wLabel);
        const days = Array.from({ length: 7 }, (_, i) => {
          const d = new Date(monday);
          d.setUTCDate(monday.getUTCDate() + i);
          return d;
        });
        // Base page number = pagine anno + quarter + mese
        const baseWeekPage = (vis.anno ? 1 : 0)
          + (vis.quarterTargets ? visibleQuarters.length : 0)
          + (vis.quarterScomp ? visibleQuarters.length : 0)
          + (vis.mese ? 1 : 0);
        return (
          <section className="page" key={`week-${wLabel}`} data-testid={`print-page-week-${wLabel}`}>
            <PageHeader anno={anno} />
            <div className="hero sm">
              <div>
                <div className="kick">Settimana</div>
                <h1>{labelSettimana(wLabel)}</h1>
              </div>
              <div className="sub">MIT<br />giornaliere</div>
            </div>

            <SectionHeader label="Le 3 cose più importanti, ogni giorno" hint="MIT" />
            <div className="dayboxes">
              {days.map((d, i) => {
                const dStr = isoDateUTC(d);
                const items = mitByDate[dStr] || [];
                const isWeekend = i >= 5;
                return (
                  <div key={dStr} className={`daybox${isWeekend ? ' we' : ''}`} data-testid={`day-${dStr}`}>
                    <div className="dayhead">
                      <span className="dname">{GIORNI_IT[i]}</span>
                      <span className="ddate">{fmtDateIt(d)}</span>
                    </div>
                    <div className="daylist">
                      {[0, 1, 2].map((slot) => {
                        const it = items[slot];
                        return (
                          <div key={slot} className="dayitem">
                            <span className="dayidx">{slot + 1}</span>
                            <span className="daytxt">{it?.testo || ''}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <Foot label={`SETT ${wLabel} · ${footPage(baseWeekPage + 1 + idx)}`} />
          </section>
        );
      })}

      {/* PAGINE GIORNO — 7 pagine dettagliate (MIT + Azioni fissate a giorno) */}
      {vis.days && (() => {
        const wLabel = currentWeekLabel;
        const monday = mondayOfIsoWeek(wLabel);
        const days = Array.from({ length: 7 }, (_, i) => {
          const d = new Date(monday);
          d.setUTCDate(monday.getUTCDate() + i);
          return d;
        });
        const azioniMese = data.azioni_mese || [];
        const azioniByDate = azioniMese.reduce((m, a) => {
          if (!a.data) return m;
          m[a.data] = m[a.data] || [];
          m[a.data].push(a);
          return m;
        }, {});
        const baseDayPage = (vis.anno ? 1 : 0)
          + (vis.quarterTargets ? visibleQuarters.length : 0)
          + (vis.quarterScomp ? visibleQuarters.length : 0)
          + (vis.mese ? 1 : 0)
          + vis.weekLabels.length;
        return days.map((d, i) => {
          const dStr = isoDateUTC(d);
          const mits = mitByDate[dStr] || [];
          const azs = azioniByDate[dStr] || [];
          return (
            <section className="page" key={`day-${dStr}`} data-testid={`print-page-day-${dStr}`}>
              <PageHeader anno={anno} />
              <div className="hero sm">
                <div>
                  <div className="kick">Giorno</div>
                  <h1>{GIORNI_IT[i]} {fmtDateIt(d)}</h1>
                </div>
                <div className="sub">Settimana<br />{labelSettimana(wLabel)}</div>
              </div>
              <SectionHeader label="Le 3 MIT del giorno" hint={`${mits.length} pianificat${mits.length === 1 ? 'a' : 'e'}`} />
              <table className="tbl day-mit"><tbody>
                {[0, 1, 2].map((slot) => {
                  const it = mits[slot];
                  return (
                    <tr key={slot}>
                      <td style={{ width: 40 }}><span className="onum">{fmtPad(slot + 1)}</span></td>
                      <td className="day-slot">{it?.testo || <span className="day-blank">— da compilare —</span>}</td>
                    </tr>
                  );
                })}
              </tbody></table>
              <SectionHeader label="Azioni fissate al giorno" hint={`${azs.length} programmat${azs.length === 1 ? 'a' : 'e'}`} />
              {azs.length === 0 ? (
                <div className="day-empty">Nessuna azione fissata a questo giorno. Le altre azioni della settimana restano flottanti.</div>
              ) : (
                <table className="tbl"><tbody>
                  {azs.map((a) => {
                    const cssVar = AREA_VAR[(a.area || '').toLowerCase()] || 'ink';
                    return (
                      <tr key={a.id}>
                        <td style={{ width: 50 }}>
                          <span className="tag" style={{ borderColor: `var(--${cssVar})`, color: `var(--${cssVar})` }}>
                            {a.is_bau ? 'BAU' : (a.area || '')}
                          </span>
                        </td>
                        <td>{a.testo}</td>
                      </tr>
                    );
                  })}
                </tbody></table>
              )}
              <Foot label={`GIORNO ${dStr} · ${footPage(baseDayPage + 1 + i)}`} />
            </section>
          );
        });
      })()}

      {/* PAGINA PORTAFOGLIO — A5 landscape foldable */}
      {vis.portafoglio && (() => {
        const wLabel = currentWeekLabel;
        const monday = mondayOfIsoWeek(wLabel);
        const days = Array.from({ length: 7 }, (_, i) => {
          const d = new Date(monday);
          d.setUTCDate(monday.getUTCDate() + i);
          return d;
        });
        // Layout: 2 colonne × 4 righe. Piegando a metà orizzontale:
        //   Panel sinistro (nascosto dentro): Lun, Mar, Mer, Gio
        //   Panel destro   (fuori/visibile):  Ven, Sab, Dom, Focus
        // Ordine flow-row: [Lun, Ven] [Mar, Sab] [Mer, Dom] [Gio, Focus]
        const grid = [
          [0, 4], [1, 5], [2, 6], [3, -1], // -1 = casella Focus/Note
        ];
        return (
          <section className="page wallet-page" data-testid="print-page-wallet">
            <div className="wallet-header">
              <div className="wallet-title">
                <span className="wm">Quantum Planner</span>
                <span className="wallet-week">{labelSettimana(wLabel)}</span>
              </div>
              <div className="wallet-fold">— piega qui →</div>
            </div>
            <div className="wallet-grid">
              {grid.map(([leftIdx, rightIdx], row) => (
                <div key={row} className="wallet-row">
                  {[leftIdx, rightIdx].map((dayIdx, col) => {
                    if (dayIdx === -1) {
                      // Casella Focus / Note settimana
                      return (
                        <div key={`focus-${col}`} className="wallet-cell wallet-focus">
                          <div className="wallet-day-head">
                            <span className="wallet-dname">Focus</span>
                            <span className="wallet-ddate">settimana</span>
                          </div>
                          <div className="wallet-notes">
                            <span className="wallet-line"></span>
                            <span className="wallet-line"></span>
                            <span className="wallet-line"></span>
                          </div>
                        </div>
                      );
                    }
                    const d = days[dayIdx];
                    const dStr = isoDateUTC(d);
                    const items = mitByDate[dStr] || [];
                    const isWeekend = dayIdx >= 5;
                    return (
                      <div key={dayIdx} className={`wallet-cell${isWeekend ? ' we' : ''}`} data-testid={`wallet-day-${dStr}`}>
                        <div className="wallet-day-head">
                          <span className="wallet-dname">{GIORNI_IT[dayIdx]}</span>
                          <span className="wallet-ddate">{fmtDateIt(d)}</span>
                        </div>
                        <div className="wallet-slots">
                          {[0, 1, 2].map((slot) => {
                            const it = items[slot];
                            return (
                              <div key={slot} className="wallet-slot">
                                <span className="wallet-slot-idx">{slot + 1}</span>
                                <span className="wallet-slot-txt">{it?.testo || ''}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="wallet-foot">
              <span>quantumplanner.it</span>
              <span>{wLabel}</span>
            </div>
          </section>
        );
      })()}
    </div>
  );
}

function PageHeader({ anno }) {
  return (
    <div className="mast">
      <span className="wm">Quantum Planner</span>
      <span className="yr">v2.0 — {anno}</span>
    </div>
  );
}

function SectionHeader({ label, hint }) {
  return (
    <div className="sec">
      <h2>{label}</h2>
      <span className="ln"></span>
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

function Foot({ label }) {
  return (
    <div className="foot">
      <span>QUANTUM PLANNER · quantumplanner.it</span>
      <span>{label}</span>
    </div>
  );
}
